// src/pages/AgentsPage.js
import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Button, IconButton,
  Table, TableHead, TableBody, TableRow, TableCell, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Snackbar, Alert, Chip, LinearProgress, FormControl, InputLabel, Select,
  MenuItem, Tab, Tabs, Checkbox, FormControlLabel, List, ListItem,
  ListItemText, ListItemSecondaryAction, Divider, Autocomplete, CircularProgress,
  Stack, TableSortLabel, Menu, ListItemIcon
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Refresh as RefreshIcon, Key as KeyIcon, Settings as SettingsIcon,
  Person as PersonIcon, Assignment as AssignmentIcon,
  Security as SecurityIcon, AdminPanelSettings as AdminPanelSettingsIcon,
  PowerSettingsNew as PowerIcon, PlayArrow as PowerOnIcon, FilterList as FilterListIcon,
  MoreVert as MoreVertIcon, UploadFile as UploadFileIcon, Download as DownloadIcon
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { ApiContext } from '../contexts/ApiContext';
import LoadingScreen from '../components/common/LoadingScreen';
import * as XLSX from 'xlsx';
import { usePermissions } from '../hooks/usePermissions';

const TRANSFER_CHANNEL_OPTIONS = [
  { value: 'agent_response', label: 'Respuesta del agente (IA)' },
  { value: 'user_transcript', label: 'Transcripción del usuario' },
  { value: 'both', label: 'Ambos canales' },
];

const SIP_CODEC_OPTIONS = ['ulaw', 'alaw', 'gsm', 'g729'];
const DEFAULT_CODEC_SELECTION = ['alaw'];
const normalizeCodecSelection = (value) => {
  const arr = Array.isArray(value) ? value : (value ? [value] : []);
  const normalized = arr
    .map((codec) => String(codec).trim().toLowerCase())
    .filter((codec) => SIP_CODEC_OPTIONS.includes(codec));
  return normalized.length ? normalized : [...DEFAULT_CODEC_SELECTION];
};



const createEmptyTransferRule = () => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  channel: 'agent_response',
  keywords: '',
  targetSkill: '',
});

const parseExtraConfig = (raw) => {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
};

const formatRulesForState = (rawRules) => {
  if (!Array.isArray(rawRules) || rawRules.length === 0) {
    return [createEmptyTransferRule()];
  }
  return rawRules.map((rule, index) => ({
    id: `${Date.now()}-${index}-${Math.random().toString(36).slice(2, 8)}`,
    channel: TRANSFER_CHANNEL_OPTIONS.some((option) => option.value === (rule?.channel || '').toLowerCase())
      ? (rule.channel || '').toLowerCase()
      : 'agent_response',
    keywords: Array.isArray(rule?.keywords) ? rule.keywords.join(', ') : (rule?.keywords || ''),
    targetSkill: rule?.targetSkill || '',
  }));
};

const serializeTransferRuleState = (rules) => {
  if (!Array.isArray(rules)) return [];
  return rules
    .map((rule) => {
      const targetSkill = (rule.targetSkill || '').trim();
      if (!targetSkill) return null;
      const keywords = (rule.keywords || '')
        .split(',')
        .map((keyword) => keyword.trim())
        .filter((keyword) => keyword.length > 0);
      if (!keywords.length) return null;
      const channel = TRANSFER_CHANNEL_OPTIONS.some((option) => option.value === rule.channel)
        ? rule.channel
        : 'agent_response';
      return {
        targetSkill,
        keywords,
        channel,
      };
    })
    .filter(Boolean);
};

const STATUS_COLORS = {
  'AVAILABLE': 'success',
  'BUSY': 'warning',
  'PAUSE': 'info',
  'OFFLINE': 'default',
  'NO_NETWORK': 'error',
  'NOTREADY': 'secondary'
};

const STATUS_LABELS = {
  'AVAILABLE': 'Disponible',
  'BUSY': 'Ocupado',
  'PAUSE': 'Pausa',
  'OFFLINE': 'Desconectado',
  'NO_NETWORK': 'Sin Red',
  'NOTREADY': 'No Disponible'
};

const AGENTS_COLUMN_OPTIONS = [
  { id: 'agent', label: 'Agente ID', sortable: true, filterable: true, type: 'string' },
  { id: 'name', label: 'Nombre', sortable: true, filterable: true, type: 'string' },
  { id: 'extension', label: 'Extensión', sortable: true, filterable: true, type: 'string' },
  { id: 'supervisor', label: 'Supervisor', sortable: true, filterable: true, type: 'string' },
  { id: 'manager', label: 'Gerente', sortable: true, filterable: true, type: 'string' },
  { id: 'skills', label: 'Skills', sortable: true, filterable: true, type: 'string' },
  { id: 'role', label: 'Rol', sortable: true, filterable: true, type: 'string' },
  { id: 'actions', label: 'Acciones', sortable: false, filterable: false, type: 'string', align: 'right' }
];

const VIRTUAL_COLUMN_OPTIONS = [
  { id: 'agent', label: 'ID Agente', sortable: true, filterable: true, type: 'string' },
  { id: 'agent_id', label: 'Agent ID', sortable: true, filterable: true, type: 'string' },
  { id: 'name', label: 'Nombre', sortable: true, filterable: true, type: 'string' },
  { id: 'extension', label: 'Extensión', sortable: true, filterable: true, type: 'string' },
  { id: 'status', label: 'Estado', sortable: true, filterable: true, type: 'string' },
  { id: 'skills', label: 'Skills', sortable: true, filterable: true, type: 'string' },
  { id: 'created_at', label: 'Fecha Creación', sortable: true, filterable: true, type: 'datetime' },
  { id: 'actions', label: 'Acciones', sortable: false, filterable: false, type: 'string', align: 'right' }
];

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 1 }}>{children}</Box>}
    </div>
  );
}

export default function AgentsPage() {
  const { agents, skills, roles, campaigns, sse } = useContext(ApiContext);
  const { canAction } = usePermissions();

  const [items, setItems] = useState([]);
  const [virtualAgents, setVirtualAgents] = useState([]);
  const [skillsList, setSkillsList] = useState([]);
  const [availableExtensions, setAvailableExtensions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [powering, setPowering] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [agentColumnFilters, setAgentColumnFilters] = useState({});
  const [agentSortConfig, setAgentSortConfig] = useState({ column: null, direction: 'asc' });
  const [activeAgentFilterColumn, setActiveAgentFilterColumn] = useState(null);
  const [virtualColumnFilters, setVirtualColumnFilters] = useState({});
  const [virtualSortConfig, setVirtualSortConfig] = useState({ column: null, direction: 'asc' });
  const [activeVirtualFilterColumn, setActiveVirtualFilterColumn] = useState(null);
  const [agentLookup, setAgentLookup] = useState({});

  // Roles state
  const [rolesList, setRolesList] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [permissionsByModule, setPermissionsByModule] = useState({});

  // Campaigns state
  const [allCampaigns, setAllCampaigns] = useState([]);
  const [agentCampaigns, setAgentCampaigns] = useState([]);

  // Hierarchy state
  const [supervisorsList, setSupervisorsList] = useState([]);
  const [managersList, setManagersList] = useState([]);

  // Dialog states
  const [openAgent, setOpenAgent] = useState(false);
  const [openVirtualAgent, setOpenVirtualAgent] = useState(false);
  const [openEditVirtualAgent, setOpenEditVirtualAgent] = useState(false);
  const [editingVirtualAgent, setEditingVirtualAgent] = useState(null);
  const [openPassword, setOpenPassword] = useState(false);
  const [openSkills, setOpenSkills] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentSkills, setAgentSkills] = useState([]);
  const [actionMenuPosition, setActionMenuPosition] = useState(null);
  const [actionMenuAgent, setActionMenuAgent] = useState(null);

  // Role management dialog states
  const [openRole, setOpenRole] = useState(false);
  const [openAssignRole, setOpenAssignRole] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [selectedRolePermissions, setSelectedRolePermissions] = useState({});

  const [virtualAgentRules, setVirtualAgentRules] = useState(() => [createEmptyTransferRule()]);
  const [editVirtualAgentRules, setEditVirtualAgentRules] = useState(() => [createEmptyTransferRule()]);
  const [openImportAgents, setOpenImportAgents] = useState(false);
  const [importPreview, setImportPreview] = useState([]);
  const [importParsing, setImportParsing] = useState(false);
  const [importErrors, setImportErrors] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importFileName, setImportFileName] = useState('');
  const [importDownloading, setImportDownloading] = useState(false);
  const importTableContainerRef = useRef(null);
  const isPanningRef = useRef(false);
  const panStartRef = useRef({ x: 0, scrollLeft: 0 });
  const [exporting, setExporting] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const { register, handleSubmit, watch, setValue, reset, unregister, control, formState: { errors } } = useForm({
    defaultValues: {
      agent: '',
      name: '',
      password: '',
      extension: '',
      document_number: '',
      supervisor_id: '',
      manager_id: '',
      role_id: ''
    }
  });

  const { register: registerRole, handleSubmit: handleSubmitRole, watch: watchRole, setValue: setValueRole, reset: resetRole, formState: { errors: errorsRole } } = useForm({
    defaultValues: {
      role_name: '',
      description: ''
    }
  });

const { register: registerVirtualAgent, handleSubmit: handleSubmitVirtualAgent, watch: watchVirtualAgent, setValue: setValueVirtualAgent, reset: resetVirtualAgent, control: controlVirtualAgent, formState: { errors: errorsVirtualAgent } } = useForm({
  defaultValues: {
    name: '',
    agent_id: '',
    allow: [...DEFAULT_CODEC_SELECTION]
  }
});

  const handleAgentSortChange = (column) => {
    setAgentSortConfig(prev => {
      if (prev.column === column) {
        return { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { column, direction: 'asc' };
    });
  };

  const handleVirtualSortChange = (column) => {
    setVirtualSortConfig(prev => {
      if (prev.column === column) {
        return { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { column, direction: 'asc' };
    });
  };

  const handleAgentColumnFilterChange = (column, value) => {
    setAgentColumnFilters(prev => ({
      ...prev,
      [column]: value
    }));
  };

  const handleVirtualColumnFilterChange = (column, value) => {
    setVirtualColumnFilters(prev => ({
      ...prev,
      [column]: value
    }));
  };

  const getAgentName = (id) => {
    if (!id) return '';
    const info = agentLookup[id];
    return info?.name || id;
  };

  const getAgentColumnFilterString = (row, column) => {
    switch (column) {
      case 'agent':
        return row.agent || '';
      case 'name':
        return row.name || '';
      case 'extension':
        return row.extension || '';
      case 'role':
        return row.role?.role_name || '';
      case 'supervisor':
        return getAgentName(row.supervisor_id);
      case 'manager':
        return getAgentName(row.manager_id);
      case 'skills':
        return Array.isArray(row.skills)
          ? row.skills.filter(s => s.status === 1).map(skill => skill.skill_name).join(' ')
          : '';
      default:
        return '';
    }
  };

  const getVirtualColumnFilterString = (row, column) => {
    switch (column) {
      case 'agent':
        return row.agent || '';
      case 'agent_id':
        return row.agent_id || '';
      case 'name':
        return row.name || '';
      case 'extension':
        return row.extension || '';
      case 'status':
        return STATUS_LABELS[row.status] || row.status || '';
      case 'skills':
        return Array.isArray(row.skills)
          ? row.skills.filter(s => s.status === 1).map(skill => skill.skill_name).join(' ')
          : '';
      case 'created_at':
        return row.created_at ? new Date(row.created_at).toLocaleDateString('es-ES') : '';
      default:
        return '';
    }
  };

  const agentFilteredItems = useMemo(() => {
    const activeFilters = Object.entries(agentColumnFilters)
      .filter(([column, value]) => AGENTS_COLUMN_OPTIONS.some(opt => opt.id === column && opt.filterable) && value?.trim());
    if (!activeFilters.length) {
      return items;
    }
    return items.filter(row =>
      activeFilters.every(([column, value]) => {
        const normalized = value.trim().toLowerCase();
        const candidate = getAgentColumnFilterString(row, column).toLowerCase();
        return candidate.includes(normalized);
      })
    );
  }, [agentColumnFilters, items]);

  const virtualFilteredItems = useMemo(() => {
    const activeFilters = Object.entries(virtualColumnFilters)
      .filter(([column, value]) => VIRTUAL_COLUMN_OPTIONS.some(opt => opt.id === column && opt.filterable) && value?.trim());
    if (!activeFilters.length) {
      return virtualAgents;
    }
    return virtualAgents.filter(row =>
      activeFilters.every(([column, value]) => {
        const normalized = value.trim().toLowerCase();
        const candidate = getVirtualColumnFilterString(row, column).toLowerCase();
        return candidate.includes(normalized);
      })
    );
  }, [virtualColumnFilters, virtualAgents]);

  const agentSortedItems = useMemo(() => {
    if (!agentSortConfig.column) {
      return agentFilteredItems;
    }
    const columnType = AGENTS_COLUMN_OPTIONS.find(opt => opt.id === agentSortConfig.column)?.type || 'string';
    const data = [...agentFilteredItems];
    const getSortValue = (row) => {
      switch (agentSortConfig.column) {
        case 'agent':
          return row.agent || '';
        case 'name':
          return row.name || '';
        case 'extension':
          return row.extension || '';
        case 'role':
          return row.role?.role_name || '';
        case 'supervisor':
          return getAgentName(row.supervisor_id);
        case 'manager':
          return getAgentName(row.manager_id);
        case 'skills':
          return Array.isArray(row.skills)
            ? row.skills.filter(s => s.status === 1).length
            : 0;
        default:
          return '';
      }
    };
    data.sort((a, b) => {
      const valueA = getSortValue(a);
      const valueB = getSortValue(b);
      if (columnType === 'number') {
        const diff = (Number(valueA) || 0) - (Number(valueB) || 0);
        return agentSortConfig.direction === 'asc' ? diff : -diff;
      }
      const stringA = (valueA || '').toString().toLowerCase();
      const stringB = (valueB || '').toString().toLowerCase();
      if (stringA === stringB) return 0;
      if (stringA > stringB) {
        return agentSortConfig.direction === 'asc' ? 1 : -1;
      }
      return agentSortConfig.direction === 'asc' ? -1 : 1;
    });
    return data;
  }, [agentFilteredItems, agentSortConfig]);

  const allVisibleAgentsSelected = false;
  const isAgentSelectionIndeterminate = false;

  const openActionMenu = (event, agent) => {
    event.preventDefault();
    const { clientX, clientY, currentTarget } = event;
    let mouseX = clientX;
    let mouseY = clientY;
    if (event.type === 'click' && currentTarget) {
      const rect = currentTarget.getBoundingClientRect();
      mouseX = rect.left;
      mouseY = rect.top + rect.height;
    }
    setActionMenuPosition({ mouseX, mouseY });
    setActionMenuAgent(agent);
  };

  const closeActionMenu = () => {
    setActionMenuPosition(null);
    setActionMenuAgent(null);
  };

  const virtualSortedItems = useMemo(() => {
    if (!virtualSortConfig.column) {
      return virtualFilteredItems;
    }
    const columnType = VIRTUAL_COLUMN_OPTIONS.find(opt => opt.id === virtualSortConfig.column)?.type || 'string';
    const data = [...virtualFilteredItems];
    const getSortValue = (row) => {
      switch (virtualSortConfig.column) {
        case 'agent':
          return row.agent || '';
        case 'agent_id':
          return row.agent_id || '';
        case 'name':
          return row.name || '';
        case 'extension':
          return row.extension || '';
        case 'status':
          return STATUS_LABELS[row.status] || row.status || '';
        case 'skills':
          return Array.isArray(row.skills)
            ? row.skills.filter(s => s.status === 1).length
            : 0;
        case 'created_at':
          return row.created_at ? new Date(row.created_at).getTime() : 0;
        default:
          return '';
      }
    };
    data.sort((a, b) => {
      const valueA = getSortValue(a);
      const valueB = getSortValue(b);
      if (columnType === 'number' || columnType === 'datetime') {
        const diff = (Number(valueA) || 0) - (Number(valueB) || 0);
        return virtualSortConfig.direction === 'asc' ? diff : -diff;
      }
      const stringA = (valueA || '').toString().toLowerCase();
      const stringB = (valueB || '').toString().toLowerCase();
      if (stringA === stringB) return 0;
      if (stringA > stringB) {
        return virtualSortConfig.direction === 'asc' ? 1 : -1;
      }
      return virtualSortConfig.direction === 'asc' ? -1 : 1;
    });
    return data;
  }, [virtualFilteredItems, virtualSortConfig]);

  const { register: registerEditVirtualAgent, handleSubmit: handleSubmitEditVirtualAgent, watch: watchEditVirtualAgent, setValue: setValueEditVirtualAgent, reset: resetEditVirtualAgent, formState: { errors: errorsEditVirtualAgent } } = useForm({
    defaultValues: {
      name: '',
      agent_id: ''
    }
  });

  const show = (m, s='success') => setSnackbar({ open: true, message: m, severity: s });
  const closeSnack = () => setSnackbar(prev => ({ ...prev, open: false }));

  const resetVirtualAgentRuleState = () => setVirtualAgentRules([createEmptyTransferRule()]);
  const resetEditVirtualAgentRuleState = (rules = []) => setEditVirtualAgentRules(formatRulesForState(rules));

  const updateTransferRuleState = (mode, updater) => {
    if (mode === 'edit') {
      setEditVirtualAgentRules((prev) => updater(prev));
    } else {
      setVirtualAgentRules((prev) => updater(prev));
    }
  };

  const handleTransferRuleChange = (mode, id, field, value) => {
    updateTransferRuleState(mode, (prev) => prev.map((rule) => (rule.id === id ? { ...rule, [field]: value } : rule)));
  };

  const handleAddTransferRule = (mode) => {
    updateTransferRuleState(mode, (prev) => [...prev, createEmptyTransferRule()]);
  };

  const handleRemoveTransferRule = (mode, id) => {
    updateTransferRuleState(mode, (prev) => (prev.length <= 1 ? prev : prev.filter((rule) => rule.id !== id)));
  };

  const buildTransferRulesPayload = (mode) => {
    const state = mode === 'edit' ? editVirtualAgentRules : virtualAgentRules;
    return serializeTransferRuleState(state);
  };

  const renderTransferRuleForms = (rules, mode) => (
    <>
      {rules.map((rule, index) => {
        const selectLabelId = `transfer-channel-${mode}-${rule.id}`;
        return (
          <Grid item xs={12} key={rule.id}>
            <Card variant="outlined" sx={{ mb: 1 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2">Regla #{index + 1}</Typography>
                  {rules.length > 1 && (
                    <Button
                      size="small"
                      color="secondary"
                      onClick={() => handleRemoveTransferRule(mode, rule.id)}
                    >
                      Eliminar
                    </Button>
                  )}
                </Box>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel id={selectLabelId}>Canal a monitorear</InputLabel>
                      <Select
                        labelId={selectLabelId}
                        label="Canal a monitorear"
                        value={rule.channel}
                        onChange={(e) => handleTransferRuleChange(mode, rule.id, 'channel', e.target.value)}
                      >
                        {TRANSFER_CHANNEL_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      label="Palabras clave"
                      fullWidth
                      value={rule.keywords}
                      onChange={(e) => handleTransferRuleChange(mode, rule.id, 'keywords', e.target.value)}
                      helperText="Separadas por comas. Ej: bienvenido, transferencia"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Skill / Contexto de destino"
                      fullWidth
                      value={rule.targetSkill}
                      onChange={(e) => handleTransferRuleChange(mode, rule.id, 'targetSkill', e.target.value)}
                      helperText="Nombre del contexto en el dialplan al que se transferirá la llamada"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
      <Grid item xs={12}>
        <Button variant="outlined" onClick={() => handleAddTransferRule(mode)}>
          Agregar regla
        </Button>
      </Grid>
    </>
  );

  const loadAll = async () => {
    try {
      setLoading(true);
      const [agentsRes, skillsRes, extensionsRes, rolesRes] = await Promise.all([
        agents.getAll(),
        skills.getAll(),
        agents.getAvailableExtensions(),
        roles.getAll()
      ]);

      const allAgents = agentsRes?.data?.agents || [];
      setItems(allAgents.filter(agent => !agent.virtual));
      setVirtualAgents(allAgents.filter(agent => agent.virtual));
      const lookup = {};
      allAgents.forEach(agent => {
        lookup[agent.agent] = { name: agent.name || agent.agent };
      });
      setAgentLookup(lookup);
      setSkillsList(skillsRes?.data?.skills || []);
      setAvailableExtensions(extensionsRes?.data?.extensions || []);
      setRolesList(rolesRes?.data?.roles || []);

      // Filtrar usuarios que pueden ser supervisores/gerentes (roles que NO sean 'Agent' o 'agente')
      const nonAgentUsers = allAgents.filter(agent => {
        const roleName = agent.role?.role_name?.toLowerCase() || '';
        return !agent.virtual && roleName && roleName !== 'agent' && roleName !== 'agente';
      });
      // Si no hay roles asignados que cumplan, permitir todos los agentes no virtuales como fallback
      const fallbackHierarchyPool = allAgents.filter(agent => !agent.virtual);
      const hierarchyPool = nonAgentUsers.length ? nonAgentUsers : fallbackHierarchyPool;
      setSupervisorsList(hierarchyPool);
      setManagersList(hierarchyPool);
    } catch (e) {
      console.error('Error loading data:', e);
      show(`Error cargando datos: ${e.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const data = await roles.getAll();
      if (data.success) {
        setRolesList(data.data.roles || []);
      }
    } catch (e) {
      console.error('Error loading roles:', e);
      show(`Error cargando roles: ${e.message}`, 'error');
    }
  };

  const loadPermissions = async () => {
    try {
      const data = await roles.getPermissions();
      if (data.success) {
        setPermissions(data.data.permissions || []);
        setPermissionsByModule(data.data.permissionsByModule || {});
      }
    } catch (e) {
      console.error('Error loading permissions:', e);
      show(`Error cargando permisos: ${e.message}`, 'error');
    }
  };

  const onSubmitRole = async (data) => {
    try {
      const permissionsArray = Object.entries(selectedRolePermissions).map(([permissionId, perms]) => ({
        permission_id: parseInt(permissionId),
        ...perms
      }));

      const payload = {
        ...data,
        permissions: permissionsArray
      };

      const result = editingRole
        ? await roles.update(editingRole.id, payload)
        : await roles.create(payload);

      if (result.success) {
        show(editingRole ? 'Rol actualizado correctamente' : 'Rol creado correctamente');
        setOpenRole(false);
        resetRole();
        setEditingRole(null);
        setSelectedRolePermissions({});
        loadRoles();
      } else {
        throw new Error(result.message || 'Error en la operación');
      }
    } catch (e) {
      console.error('Error saving role:', e);
      show(`Error guardando rol: ${e.message}`, 'error');
    }
  };

  const onSubmitVirtualAgent = async (data) => {
    if (submitting) return;

    try {
      setSubmitting(true);

      const transferRulesPayload = buildTransferRulesPayload('create');
      const codecSelection = normalizeCodecSelection(data.allow);
      const payload = {
        name: data.name,
        agent_id: data.agent_id,
        allow: codecSelection
      };
      if (transferRulesPayload.length) {
        payload.extra_config = { transfer_rules: transferRulesPayload };
      }

      const result = await agents.createVirtual(payload);

      if (result.success) {
        show('Agente virtual creado correctamente');
        setOpenVirtualAgent(false);
        resetVirtualAgent({ name: '', agent_id: '', allow: [...DEFAULT_CODEC_SELECTION] });
        resetVirtualAgentRuleState();
        loadAll();
      } else {
        throw new Error(result.message || 'Error creando agente virtual');
      }
    } catch (e) {
      console.error('Error creating virtual agent:', e);
      show(`Error creando agente virtual: ${e.message}`, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteVirtualAgent = async (agent) => {
    if (!window.confirm(`¿ELIMINAR PERMANENTEMENTE el agente virtual "${agent.name}" (${agent.agent})?\n\nEsta acción no se puede deshacer.`)) return;

    try {
      setDeleting(true);
      await agents.delete(agent.agent);
      show('Agente virtual eliminado permanentemente');
      loadAll();
    } catch (e) {
      console.error('Error deleting virtual agent:', e);
      show(`Error eliminando agente virtual: ${e.message}`, 'error');
    } finally {
      setDeleting(false);
    }
  };

  const deleteRole = async (role) => {
    if (!window.confirm(`¿Eliminar rol "${role.role_name}"?`)) return;

    try {
      const result = await roles.delete(role.id);

      if (result.success) {
        show('Rol eliminado correctamente');
        loadRoles();
      } else {
        throw new Error(result.message || 'Error eliminando rol');
      }
    } catch (e) {
      console.error('Error deleting role:', e);
      show(`Error eliminando rol: ${e.message}`, 'error');
    }
  };

  const openEditRole = (role) => {
    setEditingRole(role);
    setValueRole('role_name', role.role_name);
    setValueRole('description', role.description || '');

    // Cargar permisos del rol
    const rolePermissions = {};
    if (role.permissionsByModule) {
      Object.values(role.permissionsByModule).forEach(modulePermissions => {
        modulePermissions.forEach(perm => {
          rolePermissions[perm.id] = {
            can_view: perm.can_view,
            can_create: perm.can_create,
            can_edit: perm.can_edit,
            can_delete: perm.can_delete
          };
        });
      });
    }
    setSelectedRolePermissions(rolePermissions);
    setOpenRole(true);
  };

  const openNewRole = () => {
    setEditingRole(null);
    resetRole();
    setSelectedRolePermissions({});
    setOpenRole(true);
  };

  const openNewVirtualAgent = () => {
    resetVirtualAgent({ name: '', agent_id: '', allow: [...DEFAULT_CODEC_SELECTION] });
    resetVirtualAgentRuleState();
    setOpenVirtualAgent(true);
  };

  const handleOpenEditVirtualAgent = (agent) => {
    setEditingVirtualAgent(agent);
    setValueEditVirtualAgent('name', agent.name);
    setValueEditVirtualAgent('agent_id', agent.agent_id || '');
    const config = parseExtraConfig(agent.extra_config);
    resetEditVirtualAgentRuleState(config.transfer_rules || []);
    setOpenEditVirtualAgent(true);
  };

  const onSubmitEditVirtualAgent = async (data) => {
    if (!editingVirtualAgent) return;

    setEditing(true);
    try {
      const transferRulesPayload = buildTransferRulesPayload('edit');
      const payload = {
        name: data.name,
        agent_id: data.agent_id,
      };
      if (transferRulesPayload.length) {
        payload.extra_config = { transfer_rules: transferRulesPayload };
      }

      const result = await agents.updateVirtual(editingVirtualAgent.agent, payload);
      if (result.success) {
        show('Agente virtual actualizado exitosamente');
        loadAll();
        setOpenEditVirtualAgent(false);
        resetEditVirtualAgent();
        setEditingVirtualAgent(null);
        resetEditVirtualAgentRuleState();
      } else {
        throw new Error(result.message || 'Error actualizando agente virtual');
      }
    } catch (e) {
      console.error('Error updating virtual agent:', e);
      show(`Error actualizando agente virtual: ${e.message}`, 'error');
    } finally {
      setEditing(false);
    }
  };

  const powerOnVirtualAgent = async (agent) => {
    setPowering(true);
    try {
      const result = await agents.powerOnVirtual(agent.agent);
      if (result.success) {
        show('Agente virtual encendido exitosamente');
        loadAll(); // Recargar agentes para mostrar el estado actualizado
      } else {
        throw new Error(result.message || 'Error encendiendo agente virtual');
      }
    } catch (e) {
      console.error('Error powering on virtual agent:', e);
      show(`Error encendiendo agente virtual: ${e.message}`, 'error');
    } finally {
      setPowering(false);
    }
  };

  const powerOffVirtualAgent = async (agent) => {
    setPowering(true);
    try {
      const result = await agents.powerOffVirtual(agent.agent);
      if (result.success) {
        show('Agente virtual apagado exitosamente');
        loadAll(); // Recargar agentes para mostrar el estado actualizado
      } else {
        throw new Error(result.message || 'Error apagando agente virtual');
      }
    } catch (e) {
      console.error('Error powering off virtual agent:', e);
      show(`Error apagando agente virtual: ${e.message}`, 'error');
    } finally {
      setPowering(false);
    }
  };

  const assignRoleToAgent = async (agentId, roleId) => {
    try {
      const result = await roles.assignToAgent(agentId, roleId);

      if (result.success) {
        show('Rol asignado correctamente al agente');
        loadAll(); // Reload agents to show updated role
        setOpenAssignRole(false);
      } else {
        throw new Error(result.message || 'Error asignando rol');
      }
    } catch (e) {
      console.error('Error assigning role:', e);
      show(`Error asignando rol: ${e.message}`, 'error');
    }
  };

  const onSubmitAgent = async (data) => {
    if (submitting) return; // Prevenir doble envío

    try {
      setSubmitting(true);

      // Remover role_id del data para enviarlo al backend
      const { role_id, ...agentData } = data;

      if (editingAgent) {
        await agents.update(editingAgent.agent, agentData);

        // Si hay un rol seleccionado, asignarlo
        if (role_id) {
          await roles.assignToAgent(editingAgent.agent, role_id);
        }

        // Guardar campañas asignadas
        try {
          await campaigns.setAgentAssignments(editingAgent.agent, agentCampaigns);
        } catch (e) {
          console.warn('Error saving campaign assignments:', e);
        }

        show('Agente actualizado correctamente');
      } else {
        await agents.create(agentData);

        // Si hay un rol seleccionado, asignarlo al agente recién creado
        if (role_id) {
          await roles.assignToAgent(data.agent, role_id);
        }

        // Guardar campañas asignadas al agente recién creado
        if (agentCampaigns.length > 0) {
          try {
            await campaigns.setAgentAssignments(data.agent, agentCampaigns);
          } catch (e) {
            console.warn('Error saving campaign assignments:', e);
          }
        }

        show('Agente creado correctamente');
      }

      setOpenAgent(false);
      reset();
      setEditingAgent(null);
      loadAll();
    } catch (e) {
      console.error('Error saving agent:', e);
      show(`Error guardando agente: ${e.message}`, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteAgent = async (agent) => {
    if (!window.confirm(`¿ELIMINAR PERMANENTEMENTE el agente "${agent.name}" (${agent.agent})?\n\nEsta acción no se puede deshacer y eliminará todos los datos del agente.`)) return;
    if (deleting) return; // Prevenir múltiples eliminaciones

    try {
      setDeleting(true);
      await agents.delete(agent.agent);
      show('Agente eliminado permanentemente');
      loadAll();
    } catch (e) {
      console.error('Error deleting agent:', e);
      show(`Error eliminando agente: ${e.message}`, 'error');
    } finally {
      setDeleting(false);
    }
  };

  const openEditAgent = async (agent) => {
    setEditingAgent(agent);
    setValue('agent', agent.agent);
    setValue('name', agent.name);
    setValue('extension', agent.extension?.trim());
    setValue('document_number', agent.document_number || '');
    setValue('supervisor_id', agent.supervisor_id || '');
    setValue('manager_id', agent.manager_id || '');
    setValue('role_id', agent.role?.id || '');

    // ELIMINAR COMPLETAMENTE el campo password del formulario en modo edición
    unregister('password');

    // Cargar extensiones, roles y campañas disponibles para edición
    try {
      const [extensionsRes, rolesRes, campaignsRes, agentCampaignsRes] = await Promise.all([
        agents.getAvailableExtensions(),
        roles.getAll(),
        campaigns.getAll(),
        campaigns.getAgentAssignments(agent.agent).catch(() => ({ data: { campaigns: [] } }))
      ]);

      const allExtensions = extensionsRes?.data?.extensions || [];

      // Limpiar extensión del agente y incluirla en las opciones disponibles
      const cleanAgentExtension = agent.extension?.trim();
      const currentExtension = { value: cleanAgentExtension, label: cleanAgentExtension };
      const extensionsWithCurrent = allExtensions.some(ext => ext.value?.trim() === cleanAgentExtension)
        ? allExtensions
        : [currentExtension, ...allExtensions];

      setAvailableExtensions(extensionsWithCurrent);
      setRolesList(rolesRes?.data?.roles || []);
      setAllCampaigns(campaignsRes?.data?.campaigns || []);
      setAgentCampaigns((agentCampaignsRes?.data?.campaigns || []).map(c => c.idcampaign));
    } catch (e) {
      console.error('Error loading available data:', e);
    }

    setOpenAgent(true);
  };

  const openNewAgent = async () => {
    setEditingAgent(null);
    reset();
    setAgentCampaigns([]);
    // Refrescar extensiones, roles y campañas disponibles al crear nuevo agente
    try {
      const [extensionsRes, rolesRes, campaignsRes] = await Promise.all([
        agents.getAvailableExtensions(),
        roles.getAll(),
        campaigns.getAll()
      ]);
      setAvailableExtensions(extensionsRes?.data?.extensions || []);
      setRolesList(rolesRes?.data?.roles || []);
      setAllCampaigns(campaignsRes?.data?.campaigns || []);
    } catch (e) {
      console.error('Error loading available data:', e);
    }
    setOpenAgent(true);
  };

  const openPasswordDialog = (agent) => {
    setSelectedAgent(agent);
    setOpenPassword(true);
  };

  const changePassword = async (event) => {
    event.preventDefault();
    const password = event.target.password.value;
    
    if (!password || password.length < 6) {
      show('La contraseña debe tener al menos 6 caracteres', 'error');
      return;
    }

    try {
      await agents.changePassword(selectedAgent.agent, password);
      show('Contraseña actualizada');
      setOpenPassword(false);
      setSelectedAgent(null);
    } catch (e) {
      show(`Error cambiando contraseña: ${e.message}`, 'error');
    }
  };

  const loadAgentSkills = async (agent) => {
    try {
      const response = await agents.getSkills(agent.agent);
      setAgentSkills(response?.data?.skills || []);
      setSelectedAgent(agent);
      setOpenSkills(true);
    } catch (e) {
      show(`Error cargando skills: ${e.message}`, 'error');
    }
  };

  const assignSkill = async (skillId, priority = 1) => {
    try {
      await agents.assignSkill(selectedAgent.agent, { id_skill: skillId, priority });
      show('Skill asignado');
      loadAgentSkills(selectedAgent);
    } catch (e) {
      show(`Error asignando skill: ${e.message}`, 'error');
    }
  };

  const unassignSkill = async (skillId) => {
    try {
      await agents.unassignSkill(selectedAgent.agent, skillId);
      show('Skill desasignado');
      loadAgentSkills(selectedAgent);
    } catch (e) {
      show(`Error desasignando skill: ${e.message}`, 'error');
    }
  };

  const updateSkillPriority = async (skillId, priority) => {
    try {
      await agents.updateSkill(selectedAgent.agent, skillId, { priority });
      show('Prioridad actualizada');
      loadAgentSkills(selectedAgent);
    } catch (e) {
      show(`Error actualizando prioridad: ${e.message}`, 'error');
    }
  };

  const normalizeImportValue = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'number') return String(value).trim();
    if (typeof value === 'string') return value.trim();
    return String(value || '').trim();
  };

  const validateImportRows = (rows) => {
    const existingAgentIds = new Set(
      [...items, ...virtualAgents].map((a) => normalizeImportValue(a.agent)).filter(Boolean)
    );
    const existingExtensions = new Set(
      [...items, ...virtualAgents].map((a) => normalizeImportValue(a.extension)).filter(Boolean)
    );
    const availableExtValues = availableExtensions
      .map((opt) => normalizeImportValue(opt.value || opt.exten || opt))
      .filter(Boolean);

    const agentCounts = rows.reduce((acc, row) => {
      const id = normalizeImportValue(row.agent);
      if (id) acc[id] = (acc[id] || 0) + 1;
      return acc;
    }, {});
    const usedPreviewExts = new Set();

    return rows.map((row) => {
      const agentId = normalizeImportValue(row.agent);
      let extension = normalizeImportValue(row.extension);
      const name = normalizeImportValue(row.name);
      const doc = normalizeImportValue(row.document_number);
      const supervisor = normalizeImportValue(row.supervisor_id);
      const manager = normalizeImportValue(row.manager_id);
      const errors = [];
      const warnings = [];
      let autoAssignedExt = false;

      if (!agentId) errors.push('ID de agente requerido');
      if (!name) errors.push('Nombre requerido');
      if (!row.password) errors.push('Contraseña requerida');
      if (agentId && agentCounts[agentId] > 1) errors.push('ID duplicado en el archivo');
      if (agentId && existingAgentIds.has(agentId)) errors.push('ID ya existe en el sistema');

      const needsExtension =
        !extension ||
        existingExtensions.has(extension) ||
        usedPreviewExts.has(extension) ||
        !availableExtValues.includes(extension);

      if (needsExtension) {
        const candidate = availableExtValues.find(
          (ext) => !existingExtensions.has(ext) && !usedPreviewExts.has(ext)
        );
        if (candidate) {
          extension = candidate;
          autoAssignedExt = true;
        } else {
          extension = '';
        }
      }

      if (!extension) errors.push('Selecciona una extensión');
      if (extension) {
        usedPreviewExts.add(extension);
      }
      if (autoAssignedExt && extension) warnings.push(`Extensión ajustada automáticamente a ${extension}`);

      if (supervisor && !agentLookup[supervisor]) {
        warnings.push('Supervisor no encontrado en el sistema');
      }
      if (manager && !agentLookup[manager]) {
        warnings.push('Gerente no encontrado en el sistema');
      }
      if (extension) {
        const isAvailable = availableExtensions.some(
          (opt) => normalizeImportValue(opt.value) === extension
        );
        if (!isAvailable && !existingExtensions.has(extension) && !autoAssignedExt) {
          warnings.push('Extensión no está en la lista de extensiones libres');
        }
      }

      return {
        ...row,
        agent: agentId,
        extension,
        name,
        document_number: doc,
        supervisor_id: supervisor,
        manager_id: manager,
        errors,
        warnings
      };
    });
  };

  const extensionOptionForValue = (value) => {
    const normalized = normalizeImportValue(value);
    if (!normalized) return null;
    return (
      availableExtensions.find((opt) => normalizeImportValue(opt.value) === normalized) || {
        value: normalized,
        label: normalized
      }
    );
  };

  const handleImportFile = async (file) => {
    if (!file) return;
    setImportParsing(true);
    setImportErrors([]);
    setImportFileName(file.name);
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames?.[0];
      if (!sheetName) {
        setImportErrors(['El archivo no contiene hojas']);
        setImportPreview([]);
        return;
      }
      const sheet = workbook.Sheets[sheetName];
      const rawRows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
      if (!rawRows.length) {
        setImportErrors(['No se encontraron filas en el archivo']);
        setImportPreview([]);
        return;
      }

      const mappedRows = rawRows
        .map((row, idx) => {
          const mapped = {
            id: `${Date.now()}-${idx}`,
            agent: '',
            name: '',
            password: '',
            extension: '',
            document_number: '',
            supervisor_id: '',
            manager_id: ''
          };

          Object.entries(row).forEach(([rawKey, rawValue]) => {
            const key = normalizeImportValue(rawKey).toLowerCase();
            const value = normalizeImportValue(rawValue);
            switch (key) {
              case 'idagente':
              case 'agente':
              case 'agent':
              case 'id agente':
                mapped.agent = value;
                break;
              case 'documento':
              case 'document_number':
                mapped.document_number = value;
                break;
              case 'nombre':
              case 'name':
                mapped.name = value;
                break;
              case 'contraseña':
              case 'contrasena':
              case 'password':
                mapped.password = value;
                break;
              case 'extension':
                mapped.extension = value;
                break;
              case 'supervisor':
              case 'supervisor_id':
                mapped.supervisor_id = value;
                break;
              case 'gerente':
              case 'manager':
              case 'manager_id':
                mapped.manager_id = value;
                break;
              default:
                break;
            }
          });

          const hasValues = Object.values(mapped).some((val) => normalizeImportValue(val));
          return hasValues ? mapped : null;
        })
        .filter(Boolean);

      if (!mappedRows.length) {
        setImportErrors(['No se encontraron datos válidos en el archivo']);
        setImportPreview([]);
        return;
      }

      const validated = validateImportRows(mappedRows);
      setImportPreview(validated);
    } catch (error) {
      console.error('Error parsing import file:', error);
      setImportErrors([`No se pudo leer el archivo: ${error.message}`]);
      setImportPreview([]);
    } finally {
      setImportParsing(false);
    }
  };

  const handleImportRowChange = (rowId, field, value) => {
    const updated = importPreview.map((row) =>
      row.id === rowId ? { ...row, [field]: normalizeImportValue(value) } : row
    );
    setImportPreview(validateImportRows(updated));
  };

  const resetImportDialog = () => {
    setImportPreview([]);
    setImportErrors([]);
    setImportFileName('');
    setImportParsing(false);
    setImporting(false);
  };

  const handleSubmitImport = async () => {
    if (!importPreview.length) {
      show('Carga un archivo antes de importar', 'error');
      return;
    }

    const rowsWithErrors = importPreview.filter((row) => row.errors && row.errors.length > 0);
    if (rowsWithErrors.length) {
      show('Corrige los errores antes de importar', 'error');
      return;
    }

    setImporting(true);
    try {
      const payload = importPreview.map((row) => ({
        agent: row.agent,
        name: row.name,
        password: row.password,
        extension: row.extension,
        document_number: row.document_number || '',
        supervisor_id: row.supervisor_id || '',
        manager_id: row.manager_id || ''
      }));

      const result = await agents.bulkImport(payload);
      if (result.success) {
        show(result.message || 'Agentes importados correctamente');
        resetImportDialog();
        setOpenImportAgents(false);
        loadAll();
      } else {
        throw new Error(result.message || 'Error importando agentes');
      }
    } catch (error) {
      console.error('Error importing agents:', error);
      show(`Error importando agentes: ${error.message}`, 'error');
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    setImportDownloading(true);
    try {
      const blob = await agents.downloadImportTemplate();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'plantilla_agentes.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading template:', error);
      show(`No se pudo descargar la plantilla: ${error.message}`, 'error');
    } finally {
      setImportDownloading(false);
    }
  };

  const handleExportAgents = async () => {
    setExporting(true);
    try {
      const blob = await agents.exportAll();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'agentes.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting agents:', error);
      show(`No se pudo exportar agentes: ${error.message}`, 'error');
    } finally {
      setExporting(false);
    }
  };

  const handlePanStart = (event) => {
    const el = importTableContainerRef.current;
    if (!el) return;
    const tag = event.target?.tagName;
    if (tag && ['INPUT', 'TEXTAREA', 'SELECT', 'OPTION', 'BUTTON'].includes(tag)) {
      return;
    }
    isPanningRef.current = true;
    panStartRef.current = {
      x: event.clientX,
      scrollLeft: el.scrollLeft
    };
    event.preventDefault();
  };

  const handlePanMove = (event) => {
    if (!isPanningRef.current) return;
    const el = importTableContainerRef.current;
    if (!el) return;
    const dx = event.clientX - panStartRef.current.x;
    el.scrollLeft = panStartRef.current.scrollLeft - dx;
  };

  const handlePanEnd = () => {
    isPanningRef.current = false;
  };

  useEffect(() => {
    loadAll();
  }, []);

  // Load roles and permissions when roles tab is accessed
  useEffect(() => {
    if (tabValue === 3) { // Roles tab
      loadRoles();
      loadPermissions();
    }
  }, [tabValue]);

  useEffect(() => {
    if (importPreview.length) {
      setImportPreview(validateImportRows(importPreview));
    }
  }, [availableExtensions, items, virtualAgents]);

  // Set up SSE listeners for real-time updates
  useEffect(() => {
    const handleAgentSkillUpdate = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('[SSE] Agent skill update received:', data);

        // Automatically refresh the agents list when skill assignments change
        loadAll();

        // If the skills dialog is open for this agent, refresh it too
        if (selectedAgent && selectedAgent.agent === data.agentId) {
          loadAgentSkills(selectedAgent);
        }

        // Show notification
        switch (data.type) {
          case 'assigned':
            show(`Skill ${data.skill.skill_name || data.skill.id_skill} asignado al agente ${data.agentId}`, 'success');
            break;
          case 'unassigned':
            show(`Skill ${data.skill.skill_name || data.skill.id_skill} desasignado del agente ${data.agentId}`, 'info');
            break;
          case 'updated':
            show(`Asignación de skill actualizada para agente ${data.agentId}`, 'info');
            break;
          default:
            show('Cambio en asignación de skill detectado', 'info');
        }
      } catch (error) {
        console.error('[SSE] Error parsing agent skill update:', error);
      }
    };

    const handleAgentUpdate = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('[SSE] Agent update received:', data);

        // Refresh agents list
        loadAll();

        show(`Agente ${data.agent?.name || data.agent?.agent || 'desconocido'} actualizado`, 'info');
      } catch (error) {
        console.error('[SSE] Error parsing agent update:', error);
      }
    };

    const handleRefresh = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('[SSE] Refresh event received:', data);

        // Force reload all data
        loadAll();

        if (selectedAgent) {
          loadAgentSkills(selectedAgent);
        }

        show('Datos actualizados automáticamente', 'success');
      } catch (error) {
        console.error('[SSE] Error parsing refresh event:', error);
      }
    };

    if (sse) {
      sse.addEventListener('agent-skill-update', handleAgentSkillUpdate);
      sse.addEventListener('agent-update', handleAgentUpdate);
      sse.addEventListener('refresh', handleRefresh);
    }

    // Cleanup listeners on unmount
    return () => {
      if (sse) {
        sse.removeEventListener('agent-skill-update');
        sse.removeEventListener('agent-update');
        sse.removeEventListener('refresh');
      }
    };
  }, [sse, selectedAgent]);

  return (
    <Box sx={{ p: 1.5 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5" component="h1">
                  Gestión de Agentes
                </Typography>
                <Box>
                  <Button
                    startIcon={<RefreshIcon />}
                    onClick={loadAll}
                    sx={{ mr: 1 }}
                    disabled={loading}
                  >
                    Actualizar
                  </Button>
                  {canAction('import_agents') && (
                  <Button
                    startIcon={<UploadFileIcon />}
                    onClick={() => {
                      resetImportDialog();
                      setOpenImportAgents(true);
                    }}
                    sx={{ mr: 1 }}
                    variant="outlined"
                  >
                    Importar Agentes
                  </Button>
                  )}
                  {canAction('export_agents') && (
                  <Button
                    startIcon={<DownloadIcon />}
                    onClick={handleExportAgents}
                    sx={{ mr: 1 }}
                    variant="outlined"
                    disabled={exporting}
                  >
                    {exporting ? 'Exportando...' : 'Exportar Agentes'}
                  </Button>
                  )}
                  {canAction('create_agent') && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={openNewAgent}
                  >
                    Nuevo Agente
                  </Button>
                  )}
                </Box>
              </Box>

              {loading && <LinearProgress sx={{ mb: 2 }} />}

              <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
                <Tab label="Lista de Agentes" icon={<PersonIcon />} />
                <Tab label="Agentes Virtuales" icon={<PersonIcon />} />
                {canAction('view_status_summary') && (
                <Tab label="Resumen por Estado" icon={<AssignmentIcon />} />
                )}
                <Tab label="Roles y Permisos" icon={<SecurityIcon />} />
              </Tabs>

              <TabPanel value={tabValue} index={0}>
                <Paper variant="outlined" sx={{ width: '100%', overflow: 'hidden' }}>
                  <Box
                    sx={{
                      position: 'relative',
                      width: '100%',
                      overflowX: 'hidden'
                    }}
                  >
                  <Table
                    sx={{
                      width: '100%',
                      tableLayout: 'fixed',
                      '& th': { fontSize: '0.85rem', py: 0.5, px: 0.75 },
                      '& td': { fontSize: '0.70rem', py: 0.4, px: 0.5 }
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        {AGENTS_COLUMN_OPTIONS.map((column) => (
                          <TableCell
                            key={column.id}
                            align={column.align || 'left'}
                            sx={
                              column.id === 'agent'
                                ? { width: 150, minWidth: 150, whiteSpace: 'nowrap' }
                                : column.id === 'actions'
                                  ? { width: 80 }
                                  : {}
                            }
                          >
                            <Stack spacing={0.5}>
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                {column.sortable ? (
                                  <TableSortLabel
                                    active={agentSortConfig.column === column.id}
                                    direction={agentSortConfig.column === column.id ? agentSortConfig.direction : 'asc'}
                                    onClick={() => handleAgentSortChange(column.id)}
                                  >
                                    {column.label}
                                  </TableSortLabel>
                                ) : (
                                  <Typography variant="subtitle2">{column.label}</Typography>
                                )}
                                {column.filterable && (
                                  <IconButton
                                    size="small"
                                    color={agentColumnFilters[column.id] ? 'primary' : 'default'}
                                    onClick={() =>
                                      setActiveAgentFilterColumn(prev => (prev === column.id ? null : column.id))
                                    }
                                  >
                                    <FilterListIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </Stack>
                              {column.filterable && activeAgentFilterColumn === column.id && (
                                <TextField
                                  value={agentColumnFilters[column.id] || ''}
                                  onChange={(event) => handleAgentColumnFilterChange(column.id, event.target.value)}
                                  size="small"
                                  placeholder="Filtrar"
                                  variant="standard"
                                  autoFocus
                                  onBlur={() => setActiveAgentFilterColumn(null)}
                                  onKeyDown={(event) => {
                                    if (event.key === 'Escape') {
                                      event.preventDefault();
                                      setActiveAgentFilterColumn(null);
                                    }
                                  }}
                                />
                              )}
                            </Stack>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {agentSortedItems.map((item) => (
                        <TableRow
                          key={item.agent}
                          hover
                          onContextMenu={(e) => openActionMenu(e, item)}
                        >
                          <TableCell sx={{ width: 150, minWidth: 150, whiteSpace: 'nowrap' }}>
                            <Typography sx={{ fontSize: 'inherit' }} variant="subtitle2">{item.agent}</Typography>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 200 }}>
                            <Typography sx={{ whiteSpace: 'normal', wordBreak: 'break-word', fontSize: 'inherit' }} title={item.name}>{item.name}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ fontSize: 'inherit' }}>{item.extension}</Typography>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 180 }}>
                            <Typography noWrap title={getAgentName(item.supervisor_id)} sx={{ fontSize: 'inherit' }}>
                              {getAgentName(item.supervisor_id) || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 180 }}>
                            <Typography noWrap title={getAgentName(item.manager_id)} sx={{ fontSize: 'inherit' }}>
                              {getAgentName(item.manager_id) || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 220 }}>
                            {Array.isArray(item.skills) && item.skills.length > 0 ? (
                              <Typography color="text.primary" sx={{ whiteSpace: 'normal', fontSize: 'inherit' }}>
                                {item.skills
                                  .filter(s => s.status === 1)
                                  .map(skill => `${skill.skill_name}`)
                                  .join(', ')}
                              </Typography>
                            ) : (
                              <Typography color="text.secondary" sx={{ fontSize: 'inherit' }}>Sin skills</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {item.role ? (
                              <Chip
                                label={item.role.role_name}
                                size="small"
                                variant="outlined"
                                color="primary"
                                sx={{ fontSize: 'inherit' }}
                              />
                            ) : (
                              <Typography color="text.secondary" sx={{ fontSize: 'inherit' }}>Sin rol</Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={(e) => openActionMenu(e, item)}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                      {agentSortedItems.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={AGENTS_COLUMN_OPTIONS.length} align="center">
                            No hay agentes registrados
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                  </Box>
                </Paper>
                <Menu
                  open={Boolean(actionMenuPosition)}
                  onClose={closeActionMenu}
                  anchorReference="anchorPosition"
                  anchorPosition={actionMenuPosition ? { top: actionMenuPosition.mouseY, left: actionMenuPosition.mouseX } : undefined}
                >
                  {canAction('edit_agent') && (
                  <MenuItem
                    onClick={() => {
                      if (actionMenuAgent) openEditAgent(actionMenuAgent);
                      closeActionMenu();
                    }}
                  >
                    <ListItemIcon>
                      <EditIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Editar" />
                  </MenuItem>
                  )}
                  {canAction('change_password_agent') && (
                  <MenuItem
                    onClick={() => {
                      if (actionMenuAgent) openPasswordDialog(actionMenuAgent);
                      closeActionMenu();
                    }}
                  >
                    <ListItemIcon>
                      <KeyIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Cambiar contraseña" />
                  </MenuItem>
                  )}
                  {canAction('manage_skills_agent') && (
                  <MenuItem
                    onClick={() => {
                      if (actionMenuAgent) loadAgentSkills(actionMenuAgent);
                      closeActionMenu();
                    }}
                  >
                    <ListItemIcon>
                      <SettingsIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Gestionar skills" />
                  </MenuItem>
                  )}
                  {canAction('assign_role_agent') && (
                  <MenuItem
                    onClick={() => {
                      if (actionMenuAgent) {
                        setSelectedAgent(actionMenuAgent);
                        setOpenAssignRole(true);
                      }
                      closeActionMenu();
                    }}
                  >
                    <ListItemIcon>
                      <SecurityIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Asignar rol" />
                  </MenuItem>
                  )}
                  {canAction('delete_agent') && (
                  <MenuItem
                    onClick={() => {
                      if (actionMenuAgent) deleteAgent(actionMenuAgent);
                      closeActionMenu();
                    }}
                    disabled={deleting}
                  >
                    <ListItemIcon>
                      <DeleteIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Eliminar" />
                  </MenuItem>
                  )}
                </Menu>
              </TabPanel>

              <TabPanel value={tabValue} index={1}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    Agentes Virtuales
                  </Typography>
                  {canAction('create_virtual_agent') && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={openNewVirtualAgent}
                  >
                    Nuevo Agente Virtual
                  </Button>
                  )}
                </Box>

                <Paper variant="outlined" sx={{ width: '100%', overflow: 'hidden' }}>
                  <Box
                    sx={{
                      position: 'relative',
                      width: 0,
                      minWidth: '100%',
                      overflowX: 'auto'
                    }}
                  >
                  <Table sx={{ minWidth: 'max-content', tableLayout: 'auto' }}>
                    <TableHead>
                      <TableRow>
                        {VIRTUAL_COLUMN_OPTIONS.map((column) => (
                          <TableCell key={column.id} align={column.align || 'left'}>
                            <Stack spacing={0.5}>
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                {column.sortable ? (
                                  <TableSortLabel
                                    active={virtualSortConfig.column === column.id}
                                    direction={virtualSortConfig.column === column.id ? virtualSortConfig.direction : 'asc'}
                                    onClick={() => handleVirtualSortChange(column.id)}
                                  >
                                    {column.label}
                                  </TableSortLabel>
                                ) : (
                                  <Typography variant="subtitle2">{column.label}</Typography>
                                )}
                                {column.filterable && (
                                  <IconButton
                                    size="small"
                                    color={virtualColumnFilters[column.id] ? 'primary' : 'default'}
                                    onClick={() =>
                                      setActiveVirtualFilterColumn(prev => (prev === column.id ? null : column.id))
                                    }
                                  >
                                    <FilterListIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </Stack>
                              {column.filterable && activeVirtualFilterColumn === column.id && (
                                <TextField
                                  value={virtualColumnFilters[column.id] || ''}
                                  onChange={(event) => handleVirtualColumnFilterChange(column.id, event.target.value)}
                                  size="small"
                                  placeholder="Filtrar"
                                  variant="standard"
                                  autoFocus
                                  onBlur={() => setActiveVirtualFilterColumn(null)}
                                  onKeyDown={(event) => {
                                    if (event.key === 'Escape') {
                                      event.preventDefault();
                                      setActiveVirtualFilterColumn(null);
                                    }
                                  }}
                                />
                              )}
                            </Stack>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {virtualSortedItems.map((item) => (
                        <TableRow key={item.agent} hover>
                          <TableCell>
                            <Typography variant="subtitle2">{item.agent}</Typography>
                          </TableCell>
                          <TableCell>{item.agent_id || '-'}</TableCell>
                          <TableCell sx={{ maxWidth: 200 }}>
                            <Typography noWrap title={item.name}>{item.name}</Typography>
                          </TableCell>
                          <TableCell>{item.extension}</TableCell>
                          <TableCell>
                            <Chip
                              label={STATUS_LABELS[item.status] || item.status}
                              color={STATUS_COLORS[item.status] || 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {Array.isArray(item.skills) && item.skills.length > 0 ? (
                              <Box>
                                {item.skills.filter(s => s.status === 1).map((skill, idx) => (
                                  <Chip
                                    key={skill.id_skill}
                                    label={`${skill.skill_name} (${skill.priority})`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ mr: 0.5, mb: 0.5 }}
                                  />
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">Sin skills</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {item.created_at ? new Date(item.created_at).toLocaleDateString('es-ES') : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {canAction('power_virtual_agent') && (
                            <>
                            {item.status === 'OFFLINE' ? (
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => powerOnVirtualAgent(item)}
                                disabled={powering}
                                title="Encender agente virtual"
                              >
                                <PowerOnIcon fontSize="small" />
                              </IconButton>
                            ) : (
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => powerOffVirtualAgent(item)}
                                disabled={powering}
                                title="Apagar agente virtual"
                              >
                                <PowerIcon fontSize="small" />
                              </IconButton>
                            )}
                            </>
                            )}
                            {canAction('manage_skills_agent') && (
                            <IconButton
                              size="small"
                              onClick={() => loadAgentSkills(item)}
                              title="Gestión de Skills"
                            >
                              <SettingsIcon fontSize="small" />
                            </IconButton>
                            )}
                            {canAction('edit_virtual_agent') && (
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleOpenEditVirtualAgent(item)}
                              disabled={editing}
                              title="Editar agente virtual"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            )}
                            {canAction('delete_virtual_agent') && (
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => deleteVirtualAgent(item)}
                              disabled={deleting}
                              title="Eliminar agente virtual"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      {virtualSortedItems.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={8} align="center">
                            No hay agentes virtuales registrados
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                  </Box>
                </Paper>
              </TabPanel>

              <TabPanel value={tabValue} index={2}>
                <Grid container spacing={2}>
                  {Object.entries(
                    items.reduce((acc, agent) => {
                      const status = agent.status || 'OFFLINE';
                      acc[status] = (acc[status] || 0) + 1;
                      return acc;
                    }, {})
                  ).map(([status, count]) => (
                    <Grid item xs={6} sm={4} md={2} key={status}>
                      <Card>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                          <Typography variant="h4" color={`${STATUS_COLORS[status] || 'text'}.main`}>
                            {count}
                          </Typography>
                          <Typography variant="body2">
                            {STATUS_LABELS[status] || status}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </TabPanel>

              <TabPanel value={tabValue} index={3}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    Gestión de Roles y Permisos
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={openNewRole}
                  >
                    Nuevo Rol
                  </Button>
                </Box>

                <Paper variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Nombre del Rol</TableCell>
                        <TableCell>Descripción</TableCell>
                        <TableCell>Permisos</TableCell>
                        <TableCell>Agentes Asignados</TableCell>
                        <TableCell align="right">Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rolesList.map((role) => (
                        <TableRow key={role.id} hover>
                          <TableCell>
                            <Box display="flex" alignItems="center">
                              <AdminPanelSettingsIcon sx={{ mr: 1, color: 'primary.main' }} />
                              <Typography variant="subtitle2">{role.role_name}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>{role.description || '-'}</TableCell>
                          <TableCell>
                            {role.permissionsByModule && Object.keys(role.permissionsByModule).length > 0 ? (
                              <Box>
                                {Object.entries(role.permissionsByModule).map(([module, permissions]) => (
                                  <Chip
                                    key={module}
                                    label={`${module} (${permissions.length})`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ mr: 0.5, mb: 0.5 }}
                                  />
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">Sin permisos</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={items.filter(agent => agent.role?.id === role.id).length}
                              size="small"
                              color="primary"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => openEditRole(role)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => deleteRole(role)}
                              disabled={['SuperAdmin', 'Admin'].includes(role.role_name)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                      {rolesList.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            No hay roles registrados
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Paper>
              </TabPanel>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialog Crear/Editar Agente */}
      <Dialog open={openAgent} onClose={() => setOpenAgent(false)} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit(onSubmitAgent)}>
          <DialogTitle>
            {editingAgent ? 'Editar Agente' : 'Nuevo Agente'}
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="ID del Agente"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  {...register('agent', { required: 'El ID del agente es requerido' })}
                  error={!!errors.agent}
                  helperText={errors.agent?.message}
                  disabled={!!editingAgent}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Número de Documento"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  {...register('document_number')}
                  helperText="Cédula, pasaporte u otro documento de identidad"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Nombre Completo"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  {...register('name', { required: 'El nombre es requerido' })}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              </Grid>

              {!editingAgent && (
                <Grid item xs={12}>
                  <TextField
                    label="Contraseña"
                    type="password"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    {...register('password', editingAgent ? {} : { required: 'La contraseña es requerida' })}
                    error={!!errors.password}
                    helperText={errors.password?.message || 'Mínimo 6 caracteres'}
                  />
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={availableExtensions}
                  getOptionLabel={(option) => option?.label || option}
                  value={availableExtensions.find(ext => ext.value?.trim() === watch('extension')?.trim()) || null}
                  onChange={(event, newValue) => {
                    setValue('extension', newValue?.value?.trim() || '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Extensión WebRTC"
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.extension}
                      helperText={errors.extension?.message || (editingAgent ? 'Puedes cambiar a otra extensión disponible' : 'Solo extensiones WebRTC disponibles')}
                      {...register('extension', { required: 'La extensión es requerida' })}
                    />
                  )}
                  noOptionsText="No hay extensiones WebRTC disponibles"
                  isOptionEqualToValue={(option, value) => option?.value?.trim() === value?.value?.trim()}
                />
              </Grid>

              {canAction('assign_role_agent') && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Rol del Agente</InputLabel>
                  <Select
                    value={watch('role_id') || ''}
                    onChange={(e) => setValue('role_id', e.target.value)}
                    label="Rol del Agente"
                  >
                    <MenuItem value="">
                      <em>Sin rol asignado</em>
                    </MenuItem>
                    {rolesList.map((role) => (
                      <MenuItem key={role.id} value={role.id}>
                        <Box display="flex" alignItems="center">
                          <AdminPanelSettingsIcon sx={{ mr: 1, fontSize: 'small' }} />
                          <Box>
                            <Typography variant="subtitle2">{role.role_name}</Typography>
                            {role.description && (
                              <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                                {role.description}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              )}

              <Grid item xs={12} md={6}>
                <Controller
                  name="supervisor_id"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      options={supervisorsList}
                      getOptionLabel={(option) => option?.name ? `${option.name} (${option.agent})` : ''}
                      value={supervisorsList.find(sup => sup.agent === field.value) || null}
                      onChange={(_, newValue) => field.onChange(newValue?.agent || '')}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Supervisor"
                          InputLabelProps={{ shrink: true }}
                          helperText="Selecciona el supervisor de este agente"
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props}>
                          <Box>
                            <Typography variant="body2" fontWeight={500}>{option.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.agent} - {option.role?.role_name || 'Sin rol'}
                            </Typography>
                          </Box>
                        </li>
                      )}
                      noOptionsText="No hay supervisores disponibles"
                      isOptionEqualToValue={(option, value) => option?.agent === value?.agent}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="manager_id"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      options={managersList}
                      getOptionLabel={(option) => option?.name ? `${option.name} (${option.agent})` : ''}
                      value={managersList.find(mgr => mgr.agent === field.value) || null}
                      onChange={(_, newValue) => field.onChange(newValue?.agent || '')}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Gerente"
                          InputLabelProps={{ shrink: true }}
                          helperText="Selecciona el gerente de este agente"
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props}>
                          <Box>
                            <Typography variant="body2" fontWeight={500}>{option.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.agent} - {option.role?.role_name || 'Sin rol'}
                            </Typography>
                          </Box>
                        </li>
                      )}
                      noOptionsText="No hay gerentes disponibles"
                      isOptionEqualToValue={(option, value) => option?.agent === value?.agent}
                    />
                  )}
                />
              </Grid>

              {canAction('assign_campaigns_agent') && (
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={allCampaigns}
                  getOptionLabel={(option) => option.descripcion || `Campaña ${option.idcampaign}`}
                  value={allCampaigns.filter(c => agentCampaigns.includes(c.idcampaign))}
                  onChange={(_, newValue) => setAgentCampaigns(newValue.map(c => c.idcampaign))}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Campañas Asignadas"
                      InputLabelProps={{ shrink: true }}
                      helperText="Si no se asignan campañas, el usuario verá todas. Seleccione campañas para restringir el acceso."
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option.descripcion}
                        size="small"
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  isOptionEqualToValue={(option, value) => option.idcampaign === value.idcampaign}
                  noOptionsText="No hay campañas disponibles"
                />
              </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenAgent(false)} disabled={submitting}>Cancelar</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
            >
              {editingAgent ? 'Actualizar' : 'Crear'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog Cambiar Contraseña */}
      <Dialog open={openPassword} onClose={() => setOpenPassword(false)} maxWidth="sm" fullWidth>
        <form onSubmit={changePassword}>
          <DialogTitle>Cambiar Contraseña</DialogTitle>
          <DialogContent>
            <TextField
              name="password"
              label="Nueva Contraseña"
              type="password"
              fullWidth
              margin="normal"
              required
              helperText="Contraseña para el agente"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenPassword(false)}>Cancelar</Button>
            <Button type="submit" variant="contained">Cambiar</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog Gestión de Skills */}
      <Dialog open={openSkills} onClose={() => setOpenSkills(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Gestión de Skills - {selectedAgent?.name} ({selectedAgent?.agent})
        </DialogTitle>
        <DialogContent>
          <Typography variant="h6" gutterBottom>Skills Asignados</Typography>
          <List>
            {agentSkills.filter(s => s.status === 1).map((skill) => (
              <ListItem key={skill.id_skill}>
                <ListItemText
                  primary={skill.skillname}
                  secondary={`Tipo: ${skill.skilltype} | Prioridad: ${skill.priority}`}
                />
                <ListItemSecondaryAction>
                  <TextField
                    type="number"
                    size="small"
                    value={skill.priority}
                    onChange={(e) => updateSkillPriority(skill.id_skill, parseInt(e.target.value))}
                    sx={{ width: 80, mr: 1 }}
                  />
                  <Button
                    size="small"
                    color="secondary"
                    onClick={() => unassignSkill(skill.id_skill)}
                  >
                    Quitar
                  </Button>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
            {agentSkills.filter(s => s.status === 1).length === 0 && (
              <ListItem>
                <ListItemText primary="No hay skills asignados" />
              </ListItem>
            )}
          </List>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="h6" gutterBottom>Asignar Nuevos Skills</Typography>
          <List>
            {skillsList
              .filter(skill => !agentSkills.some(as => as.id_skill === skill.id && as.status === 1))
              .map((skill) => (
              <ListItem key={skill.id}>
                <ListItemText
                  primary={skill.skillname}
                  secondary={`Tipo: ${skill.skilltype} | Def: ${skill.skilldef}`}
                />
                <ListItemSecondaryAction>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => assignSkill(skill.id)}
                  >
                    Asignar
                  </Button>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSkills(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Crear/Editar Rol */}
      <Dialog open={openRole} onClose={() => setOpenRole(false)} maxWidth="lg" fullWidth>
        <form onSubmit={handleSubmitRole(onSubmitRole)}>
          <DialogTitle>
            {editingRole ? 'Editar Rol' : 'Nuevo Rol'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Nombre del Rol"
                  fullWidth
                  {...registerRole('role_name', { required: 'El nombre del rol es requerido' })}
                  error={!!errorsRole.role_name}
                  helperText={errorsRole.role_name?.message}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Descripción"
                  fullWidth
                  multiline
                  rows={2}
                  {...registerRole('description')}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Permisos por Módulo
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Selecciona los permisos que tendrá este rol para cada módulo del sistema
                </Typography>

                {Object.entries(permissionsByModule).map(([moduleName, modulePermissions]) => (
                  <Card key={moduleName} sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
                        {moduleName}
                      </Typography>

                      <Grid container spacing={2}>
                        {modulePermissions.map((permission) => (
                          <Grid item xs={12} sm={6} md={3} key={permission.id}>
                            <Card variant="outlined" sx={{ p: 2 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                {permission.permission_name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {permission.description}
                              </Typography>

                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={selectedRolePermissions[permission.id]?.can_view || false}
                                    onChange={(e) => {
                                      const newPermissions = { ...selectedRolePermissions };
                                      if (!newPermissions[permission.id]) {
                                        newPermissions[permission.id] = {};
                                      }
                                      newPermissions[permission.id].can_view = e.target.checked;
                                      setSelectedRolePermissions(newPermissions);
                                    }}
                                  />
                                }
                                label="Ver"
                              />

                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={selectedRolePermissions[permission.id]?.can_create || false}
                                    onChange={(e) => {
                                      const newPermissions = { ...selectedRolePermissions };
                                      if (!newPermissions[permission.id]) {
                                        newPermissions[permission.id] = {};
                                      }
                                      newPermissions[permission.id].can_create = e.target.checked;
                                      setSelectedRolePermissions(newPermissions);
                                    }}
                                  />
                                }
                                label="Crear"
                              />

                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={selectedRolePermissions[permission.id]?.can_edit || false}
                                    onChange={(e) => {
                                      const newPermissions = { ...selectedRolePermissions };
                                      if (!newPermissions[permission.id]) {
                                        newPermissions[permission.id] = {};
                                      }
                                      newPermissions[permission.id].can_edit = e.target.checked;
                                      setSelectedRolePermissions(newPermissions);
                                    }}
                                  />
                                }
                                label="Editar"
                              />

                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={selectedRolePermissions[permission.id]?.can_delete || false}
                                    onChange={(e) => {
                                      const newPermissions = { ...selectedRolePermissions };
                                      if (!newPermissions[permission.id]) {
                                        newPermissions[permission.id] = {};
                                      }
                                      newPermissions[permission.id].can_delete = e.target.checked;
                                      setSelectedRolePermissions(newPermissions);
                                    }}
                                  />
                                }
                                label="Eliminar"
                              />
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </Card>
                ))}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenRole(false)}>Cancelar</Button>
            <Button type="submit" variant="contained">
              {editingRole ? 'Actualizar' : 'Crear'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog Crear Agente Virtual */}
      <Dialog open={openVirtualAgent} onClose={() => setOpenVirtualAgent(false)} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmitVirtualAgent(onSubmitVirtualAgent)}>
          <DialogTitle>
            Nuevo Agente Virtual
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  label="Nombre del Agente Virtual"
                  fullWidth
                  {...registerVirtualAgent('name', { required: 'El nombre es requerido' })}
                  error={!!errorsVirtualAgent.name}
                  helperText={errorsVirtualAgent.name?.message || 'Ingrese el nombre completo del agente virtual'}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Agent ID"
                  fullWidth
                  {...registerVirtualAgent('agent_id', { required: 'El Agent ID es requerido' })}
                  error={!!errorsVirtualAgent.agent_id}
                  helperText={errorsVirtualAgent.agent_id?.message || 'Ingrese el Agent ID para el agente virtual'}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  Codecs permitidos
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Selecciona los codecs SIP disponibles para la extensión creada automáticamente.
                </Typography>
                <Controller
                  name="allow"
                  control={controlVirtualAgent}
                  render={({ field }) => {
                    const value = Array.isArray(field.value) ? field.value : [];
                    const toggle = (codec) => {
                      const hasCodec = value.includes(codec);
                      const next = hasCodec ? value.filter((c) => c !== codec) : [...value, codec];
                      field.onChange(next);
                    };
                    const isChecked = (codec) => value.includes(codec);
                    return (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                        {SIP_CODEC_OPTIONS.map((codec) => (
                          <FormControlLabel
                            key={codec}
                            control={<Checkbox checked={isChecked(codec)} onChange={() => toggle(codec)} />}
                            label={codec}
                          />
                        ))}
                      </Box>
                    );
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  Reglas de transferencia por palabra clave
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Define palabras o frases que, al ser detectadas, enviarán la llamada a un contexto específico del dialplan.
                </Typography>
              </Grid>
              {renderTransferRuleForms(virtualAgentRules, 'create')}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenVirtualAgent(false)} disabled={submitting}>Cancelar</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
            >
              Crear Agente Virtual
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog Editar Agente Virtual */}
      <Dialog open={openEditVirtualAgent} onClose={() => setOpenEditVirtualAgent(false)} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmitEditVirtualAgent(onSubmitEditVirtualAgent)}>
          <DialogTitle>
            Editar Agente Virtual
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  label="Nombre del Agente Virtual"
                  fullWidth
                  {...registerEditVirtualAgent('name', { required: 'El nombre es requerido' })}
                  error={!!errorsEditVirtualAgent.name}
                  helperText={errorsEditVirtualAgent.name?.message || 'Ingrese el nombre completo del agente virtual'}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Agent ID"
                  fullWidth
                  {...registerEditVirtualAgent('agent_id', { required: 'El Agent ID es requerido' })}
                  error={!!errorsEditVirtualAgent.agent_id}
                  helperText={errorsEditVirtualAgent.agent_id?.message || 'Ingrese el Agent ID para el agente virtual'}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  Reglas de transferencia por palabra clave
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Ajusta las palabras o frases que enviarán la llamada a otro contexto del dialplan.
                </Typography>
              </Grid>
              {renderTransferRuleForms(editVirtualAgentRules, 'edit')}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenEditVirtualAgent(false)} disabled={editing}>Cancelar</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={editing}
            >
              Actualizar Agente Virtual
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog Importar Agentes */}
      <Dialog open={openImportAgents} onClose={() => { setOpenImportAgents(false); resetImportDialog(); }} maxWidth="lg" fullWidth>
        <DialogTitle>Importar agentes desde Excel</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Stack spacing={2} sx={{ maxHeight: '75vh', overflow: 'auto', p: 3, pr: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Carga un archivo Excel con las columnas: idagente, documento, nombre, contraseña, extension, supervisor, gerente.
              Todas las filas se crearán con rol Agent automáticamente.
            </Typography>
            <Box display="flex" alignItems="center" gap={2}>
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                component="label"
                disabled={importParsing}
              >
                Seleccionar archivo
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  hidden
                  onChange={(e) => handleImportFile(e.target.files?.[0])}
                />
              </Button>
              <Button
                variant="text"
                onClick={handleDownloadTemplate}
                disabled={importDownloading}
              >
                {importDownloading ? 'Descargando...' : 'Descargar plantilla'}
              </Button>
              {importFileName && (
                <Chip
                  label={importFileName}
                  onDelete={() => {
                    setImportFileName('');
                    setImportPreview([]);
                  }}
                />
              )}
              {importParsing && <CircularProgress size={24} />}
            </Box>

            {importErrors.map((err, idx) => (
              <Alert severity="error" key={idx}>{err}</Alert>
            ))}

            {importPreview.length > 0 && (
              <>
                <Alert severity={importPreview.some((row) => row.errors?.length) ? 'warning' : 'success'}>
                  {importPreview.some((row) => row.errors?.length)
                    ? 'Hay errores que corregir antes de importar.'
                    : 'Previsualización lista, confirma para importar.'}
                </Alert>
                <Box
                  ref={importTableContainerRef}
                  onMouseDown={handlePanStart}
                  onMouseMove={handlePanMove}
                  onMouseUp={handlePanEnd}
                  onMouseLeave={handlePanEnd}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    overflowX: 'auto',
                    overflowY: 'auto',
                    maxHeight: '55vh',
                    position: 'relative',
                    cursor: 'grab'
                  }}
                >
                  <Table size="small" stickyHeader sx={{ minWidth: 1400 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>ID Agente</TableCell>
                        <TableCell>Nombre</TableCell>
                        <TableCell>Documento</TableCell>
                        <TableCell>Contraseña</TableCell>
                        <TableCell>Extensión</TableCell>
                        <TableCell>Supervisor</TableCell>
                        <TableCell>Gerente</TableCell>
                        <TableCell>Validación</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {importPreview.map((row) => {
                        const hasErrors = row.errors && row.errors.length > 0;
                        return (
                          <TableRow key={row.id} selected={hasErrors}>
                            <TableCell>
                              <TextField
                                value={row.agent}
                                fullWidth
                                sx={{ minWidth: 180 }}
                                onChange={(e) => handleImportRowChange(row.id, 'agent', e.target.value)}
                                size="small"
                                label="Agente"
                                error={hasErrors && row.errors.some((err) => err.toLowerCase().includes('id'))}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                value={row.name}
                                fullWidth
                                sx={{ minWidth: 220 }}
                                onChange={(e) => handleImportRowChange(row.id, 'name', e.target.value)}
                                size="small"
                                label="Nombre"
                                error={hasErrors && row.errors.some((err) => err.toLowerCase().includes('nombre'))}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                value={row.document_number}
                                fullWidth
                                sx={{ minWidth: 180 }}
                                onChange={(e) => handleImportRowChange(row.id, 'document_number', e.target.value)}
                                size="small"
                                label="Documento"
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                value={row.password}
                                fullWidth
                                sx={{ minWidth: 200 }}
                                onChange={(e) => handleImportRowChange(row.id, 'password', e.target.value)}
                                size="small"
                                label="Contraseña"
                                type="password"
                                error={hasErrors && row.errors.some((err) => err.toLowerCase().includes('contraseña'))}
                              />
                            </TableCell>
                            <TableCell sx={{ minWidth: 240 }}>
                              <Autocomplete
                                freeSolo
                                size="small"
                                fullWidth
                                options={[...availableExtensions, extensionOptionForValue(row.extension)].filter(Boolean)}
                                value={extensionOptionForValue(row.extension)}
                                onChange={(_, newValue) =>
                                  handleImportRowChange(
                                    row.id,
                                    'extension',
                                    typeof newValue === 'string'
                                      ? newValue
                                      : newValue?.value || newValue?.label || ''
                                  )
                                }
                                onInputChange={(_, newInputValue) =>
                                  handleImportRowChange(row.id, 'extension', newInputValue)
                                }
                                getOptionLabel={(option) => {
                                  if (typeof option === 'string') return option;
                                  return option?.label || option?.value || '';
                                }}
                                isOptionEqualToValue={(option, value) =>
                                  normalizeImportValue(option?.value || option?.label || option) ===
                                  normalizeImportValue(value?.value || value?.label || value)
                                }
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Extensión"
                                    error={hasErrors && row.errors.some((err) => err.toLowerCase().includes('extensión'))}
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell sx={{ minWidth: 220 }}>
                              <Autocomplete
                                size="small"
                                fullWidth
                                options={supervisorsList}
                                getOptionLabel={(option) => option?.name ? `${option.name} (${option.agent})` : ''}
                                value={supervisorsList.find((sup) => sup.agent === row.supervisor_id) || null}
                                onChange={(_, newValue) =>
                                  handleImportRowChange(row.id, 'supervisor_id', newValue?.agent || '')
                                }
                                isOptionEqualToValue={(option, value) => option?.agent === value?.agent}
                                renderInput={(params) => (
                                  <TextField {...params} label="Supervisor" />
                                )}
                              />
                            </TableCell>
                            <TableCell sx={{ minWidth: 220 }}>
                              <Autocomplete
                                size="small"
                                fullWidth
                                options={managersList}
                                getOptionLabel={(option) => option?.name ? `${option.name} (${option.agent})` : ''}
                                value={managersList.find((mgr) => mgr.agent === row.manager_id) || null}
                                onChange={(_, newValue) =>
                                  handleImportRowChange(row.id, 'manager_id', newValue?.agent || '')
                                }
                                isOptionEqualToValue={(option, value) => option?.agent === value?.agent}
                                renderInput={(params) => (
                                  <TextField {...params} label="Gerente" />
                                )}
                              />
                            </TableCell>
                            <TableCell sx={{ minWidth: 220 }}>
                              <Box display="flex" flexWrap="wrap" gap={0.5}>
                                {row.errors && row.errors.map((err, idx) => (
                                  <Chip key={idx} label={err} color="error" size="small" />
                                ))}
                                {row.warnings && row.warnings.map((warn, idx) => (
                                  <Chip key={`w-${idx}`} label={warn} color="warning" size="small" variant="outlined" />
                                ))}
                                {!row.errors?.length && !row.warnings?.length && (
                                  <Chip label="OK" color="success" size="small" />
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenImportAgents(false);
              resetImportDialog();
            }}
            disabled={importing || importParsing}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitImport}
            disabled={importing || importParsing || !importPreview.length || importPreview.some((row) => row.errors?.length)}
          >
            {importing ? 'Importando...' : 'Importar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Asignar Rol a Agente */}
      <Dialog open={openAssignRole} onClose={() => setOpenAssignRole(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Asignar Rol</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Asignar rol al agente: <strong>{selectedAgent?.name} ({selectedAgent?.agent})</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Rol actual: {selectedAgent?.role?.role_name || 'Sin rol'}
          </Typography>

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Seleccionar Rol</InputLabel>
            <Select
              value={selectedAgent?.role?.id || ''}
              onChange={(e) => {
                if (e.target.value) {
                  assignRoleToAgent(selectedAgent.agent, e.target.value);
                }
              }}
            >
              {rolesList.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  <Box display="flex" alignItems="center">
                    <AdminPanelSettingsIcon sx={{ mr: 1, fontSize: 'small' }} />
                    <Box>
                      <Typography variant="subtitle2">{role.role_name}</Typography>
                      {role.description && (
                        <Typography variant="body2" color="text.secondary">
                          {role.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAssignRole(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={closeSnack}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={closeSnack} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Loading overlay para operaciones */}
      {submitting && <LoadingScreen />}

    </Box>
  );
}
